import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import GalleryIcon from './GalleryIcon';
import { thumbnailScrollState, thumbnailScrollTarget } from './thumbnailUtils';

import css from './ProductGallery.css';

const stopPropagation = event => {
  event.stopPropagation();
};

const readMetrics = strip => {
  const vertical = window.getComputedStyle(strip).flexDirection === 'column';
  return {
    vertical,
    position: vertical ? strip.scrollTop : strip.scrollLeft,
    viewport: vertical ? strip.clientHeight : strip.clientWidth,
    content: vertical ? strip.scrollHeight : strip.scrollWidth,
  };
};

const GalleryThumbnails = props => {
  const { slides, activeIndex, onSelect, title, modal = false, reducedMotion = false } = props;
  const intl = useIntl();
  const stripRef = useRef(null);
  const previousRef = useRef(null);
  const nextRef = useRef(null);
  const [edges, setEdges] = useState({ canPrevious: false, canNext: false });
  const activeId = slides[activeIndex] && slides[activeIndex].id;

  const measure = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const next = thumbnailScrollState(readMetrics(strip));
    if (
      (!next.canPrevious && document.activeElement === previousRef.current) ||
      (!next.canNext && document.activeElement === nextRef.current)
    )
      strip.focus({ preventScroll: true });
    setEdges(old =>
      old.canPrevious === next.canPrevious && old.canNext === next.canNext ? old : next
    );
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return undefined;
    measure();
    strip.addEventListener('wheel', stopPropagation, { passive: true });
    window.addEventListener('resize', measure);
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(strip);
      Array.from(strip.children).forEach(child => observer.observe(child));
    }
    return () => {
      strip.removeEventListener('wheel', stopPropagation);
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [slides.length, measure]);

  useEffect(() => {
    const strip = stripRef.current;
    const button = strip && strip.children[activeIndex];
    if (!button) return;
    const outer = strip.getBoundingClientRect();
    const inner = button.getBoundingClientRect();
    if (readMetrics(strip).vertical) {
      if (inner.top < outer.top) strip.scrollTop -= outer.top - inner.top;
      if (inner.bottom > outer.bottom) strip.scrollTop += inner.bottom - outer.bottom;
    } else {
      if (inner.left < outer.left) strip.scrollLeft -= outer.left - inner.left;
      if (inner.right > outer.right) strip.scrollLeft += inner.right - outer.right;
    }
    measure();
  }, [activeIndex, activeId, slides.length, measure]);

  const scroll = direction => {
    const strip = stripRef.current;
    if (!strip) return;
    const metrics = readMetrics(strip);
    const target = thumbnailScrollTarget(metrics, direction);
    strip.scrollTo({
      ...(metrics.vertical ? { top: target } : { left: target }),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div
      className={[css.thumbnailRail, modal ? css.modalThumbnailRail : ''].join(' ')}
      onPointerDown={stopPropagation}
      onWheel={stopPropagation}
      onKeyUp={event => {
        if (/^(Arrow|Page|Home$|End$)/.test(event.key)) event.stopPropagation();
      }}
      onKeyDown={event => {
        if (/^(Arrow|Page|Home$|End$)/.test(event.key)) event.stopPropagation();
        if (event.target !== stripRef.current) return;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft' || event.key === 'PageUp')
          scroll(-1);
        else if (
          event.key === 'ArrowDown' ||
          event.key === 'ArrowRight' ||
          event.key === 'PageDown'
        )
          scroll(1);
        else return;
        event.preventDefault();
      }}
    >
      <button
        ref={previousRef}
        type="button"
        className={[css.railArrow, !edges.canPrevious ? css.railArrowHidden : ''].join(' ')}
        disabled={!edges.canPrevious}
        aria-hidden={!edges.canPrevious ? true : undefined}
        aria-label={intl.formatMessage({ id: 'ProductGallery.previousThumbnails' })}
        title={intl.formatMessage({ id: 'ProductGallery.previousThumbnails' })}
        onClick={() => scroll(-1)}
      >
        <GalleryIcon name="up" />
      </button>
      <div
        ref={stripRef}
        className={css.thumbnails}
        role="group"
        aria-label={intl.formatMessage({ id: 'ProductGallery.thumbnails' })}
        tabIndex={-1}
        onScroll={measure}
      >
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            className={[css.thumbnail, index === activeIndex ? css.thumbnailActive : ''].join(' ')}
            aria-current={index === activeIndex ? 'true' : undefined}
            aria-label={intl.formatMessage({ id: 'ProductGallery.select' }, { index: index + 1, count: slides.length, title })}
            onClick={() => onSelect(index)}
          >
            <img src={slide.thumbnail} alt="" loading="lazy" draggable={false} />
          </button>
        ))}
      </div>
      <button
        ref={nextRef}
        type="button"
        className={[css.railArrow, !edges.canNext ? css.railArrowHidden : ''].join(' ')}
        disabled={!edges.canNext}
        aria-hidden={!edges.canNext ? true : undefined}
        aria-label={intl.formatMessage({ id: 'ProductGallery.nextThumbnails' })}
        title={intl.formatMessage({ id: 'ProductGallery.nextThumbnails' })}
        onClick={() => scroll(1)}
      >
        <GalleryIcon name="down" />
      </button>
    </div>
  );
};

export default GalleryThumbnails;
